import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Backdrop from 'components/Modal/Backdrop';
import { BUTTON_VARIANT } from 'constants/interfaces';

import ErrorListContent from './components/ErrorListContent';

import type IActivePopover from './interfaces/IActivePopover';
import type IQuestionErrorModal from './interfaces/IQuestionErrorModal';

import styles from './sass/QuestionErrorModal.module.scss';

function QuestionErrorModal({
  isOpen, onClose, questionErrorList, title, errorLog, hideRecommendations,
}: IQuestionErrorModal) {
  const { t } = useTranslation();
  const [activePopover, setActivePopover] = useState<IActivePopover | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const handleShowMoreClick = useCallback((
    e: React.MouseEvent<HTMLElement>,
    popoverId: string,
    formattedValues: string[],
  ) => {
    e.stopPropagation();
    const el = e.currentTarget;
    if (activePopover?.id === popoverId) {
      setActivePopover(null);
      triggerRef.current = null;
      return;
    }
    const rect = el.getBoundingClientRect();
    triggerRef.current = el;
    setActivePopover({
      id: popoverId,
      values: formattedValues,
      position: { top: rect.bottom + 4, left: rect.left },
    });
  }, [activePopover?.id]);

  const handleShowMoreKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      (e.currentTarget as HTMLElement).click();
    }
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (activePopover) {
          setActivePopover(null);
          return;
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, activePopover]);

  useEffect(() => {
    if (!isOpen) setActivePopover(null);
  }, [isOpen]);

  useEffect(() => {
    if (!activePopover) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popupRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setActivePopover(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [activePopover]);

  return isOpen ? (
    <>
      {createPortal(
        <Backdrop>
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h2 id="modal-title" className={clsx(styles.modalTitle, { [styles.errorLog]: errorLog })}>
                {t('common.importErrors')}
              </h2>
              <div className={clsx(styles.modalBody, { [styles.errorLog]: errorLog })}>
                {errorLog
                  ? (
                    <div className={styles.errorLogContainer}>
                      <p>{errorLog}</p>
                      {!hideRecommendations && <p>{ t('common.recommendations')}</p>}
                    </div>
                  )
                  : (
                    <ErrorListContent
                      title={title}
                      questionErrorList={questionErrorList}
                      activePopover={activePopover}
                      handleShowMoreClick={handleShowMoreClick}
                      handleShowMoreKeyDown={handleShowMoreKeyDown}
                    />
                  )}
              </div>
              <div className={styles.modalFooter}>
                <Button
                  title={t('common.cancel')}
                  variant={BUTTON_VARIANT.cancel}
                  onClick={onClose}
                />
              </div>
            </div>
          </div>
        </Backdrop>,
        document.body,
      )}
      {activePopover ? createPortal(
        <div
          ref={popupRef}
          className={styles.valuesPopover}
          style={{
            position: 'fixed',
            top: activePopover.position.top,
            left: activePopover.position.left,
            zIndex: 10002,
          }}
          role="list"
          aria-label="Values"
        >
          <div className={styles.valuesPopoverContent}>
            {activePopover.values.map((value) => (
              <div key={value} className={styles.valueItem} role="listitem">
                {value}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  ) : null;
}

export default QuestionErrorModal;
